import React from 'react';
import styled from 'styled-components';
import Checkbox from 'material-ui/Checkbox';
import { NitroTextField, NitroButton } from 'nitro-components';
import _get from 'lodash/get';
import { media } from '../../../utils/style-utils';
import DemoApi from '../../../api/DemoApi';
import RequestSent from '../../../assets/images/login/RequestSent_image.svg';
import {
  validName,
  validMobileNumber,
  validLandlineNumber
} from '../../../utils/validators/GenericValidators';

const Container = styled.div`
  width: 100%;
  max-width: 674px;
  background: white;
  padding: 44px;
  border-radius: 4px;
  box-shadow: -10px 9px 21px 0 rgba(128, 152, 213, 0.08);
`;

const DemoCaption = styled.span`
  color: ${props => props.theme.color.skyBlue};
  font-family: ${props => props.theme.font.bold};
  font-size: 24px;
`;

const Row = styled.div`
  display: flex;
  flex-grow: 1;
`;

const OuterRow = styled(Row)`
  ${media.mobile`
    flex-wrap: wrap;
  `}
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const OuterColumn = styled(Column)`
  width: 50%;
  padding: 0 16px 0 0;

  ${media.mobile`
    width: 100%;
    margin-bottom: 20px;
    padding: 0;
  `}
`;

const TermsColumn = styled(OuterColumn)`
  padding: 0;
`;

const SubmitButton = styled(NitroButton)`
  margin-top: 16px;
  display: flex;
  align-self: start;
`;

const ConsentText = styled.span`
  font-size: 12px;
`;

const PrivacyPolicy = styled.a`
  margin: 0 4px;
  color: #28b277;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const SuccessContainer = styled(Container)`
  padding: 55px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SuccessHeader = styled.span`
  text-align: center;
  font-size: 24px;
  color: ${props => props.theme.color.skyBlue};
  font-family: ${props => props.theme.font.bold};
`;

const SuccessCaption = styled.p`
  text-align: center;
  margin: 8px 0 32px;
`;

export const ErrorMessage = styled.p`
  color: ${props => props.theme.color.negative};
`;

class LoginDemo extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      fullName: '',
      company: '',
      email: '',
      contactNumber: '',
      agreed: false,
      error: null,
      submitted: false,
      requesting: false
    };

    this.onSubmit = this.onSubmit.bind(this);
  }

  onChange(e, field) {
    this.setState({ [field]: e.target.value });
  }

  onSubmit(e) {
    e.preventDefault();

    const { fullName, company, email, contactNumber } = this.state;

    if (!validName(fullName)) {
      return this.setState({ error: 'Please enter a valid full name' });
    }

    if (
      !validMobileNumber(contactNumber) &&
      !validLandlineNumber(contactNumber)
    ) {
      return this.setState({ error: 'Please enter a valid contact number' });
    }

    this.setState({ requesting: true, error: null });
    const props = {
      fullName,
      company,
      email,
      contactNumber
    };
    DemoApi.requestDemo(props)
      .then(() => {
        this.setState({ submitted: true });
      })
      .catch(() => {
        this.setState({
          error:
            'Oops there seems to be an issue, please refresh the page and try again'
        });
      })
      .finally(() => {
        this.setState({ requesting: false });
      });
  }

  onCheckAgreed() {
    this.setState({ agreed: !this.state.agreed });
  }

  render() {
    if (this.state.submitted) {
      return (
        <SuccessContainer>
          <SuccessHeader>
            Thank you your request has been submitted
          </SuccessHeader>
          <SuccessCaption>
            One of our team members will be touch with you shortly.
          </SuccessCaption>
          <img src={RequestSent} alt="Success" />
        </SuccessContainer>
      );
    }

    const { requesting, error, agreed } = this.state;

    return (
      <Container>
        <form onSubmit={this.onSubmit}>
          <DemoCaption>Want to know more?</DemoCaption>
          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
          <OuterRow>
            <OuterColumn>
              <NitroTextField
                inputStyle={{ color: '#454545' }}
                floatingLabelText="Full Name"
                hintText="Enter your full name"
                label="FullName"
                required
                value={this.state.fullName}
                onChange={e => this.onChange(e, 'fullName')}
                disabled={requesting}
                fullWidth
                type="text"
              />
              <NitroTextField
                inputStyle={{ color: '#454545' }}
                floatingLabelText="Company"
                hintText="Enter your company"
                label="Company"
                required
                value={this.state.company}
                onChange={e => this.onChange(e, 'company')}
                disabled={requesting}
                fullWidth
                type="text"
              />
              <NitroTextField
                inputStyle={{ color: '#454545' }}
                floatingLabelText="Email"
                hintText="Enter your email"
                label="Email"
                required
                value={this.state.email}
                onChange={e => this.onChange(e, 'email')}
                disabled={requesting}
                fullWidth
                type="email"
              />
              <NitroTextField
                inputStyle={{ color: '#454545' }}
                floatingLabelText="Contact Number"
                hintText="Enter your contact number"
                label="ContactNumber"
                required
                value={this.state.contactNumber}
                onChange={e => this.onChange(e, 'contactNumber')}
                disabled={requesting}
                fullWidth
                type="tel"
                pattern="[- +()0-9]+"
                title="Please enter a valid phone number"
              />
            </OuterColumn>
            <TermsColumn>
              <Row>
                <Column>
                  <Checkbox
                    checked={agreed}
                    disabled={requesting}
                    onCheck={() => this.onCheckAgreed()}
                  />
                </Column>
                <Column>
                  <ConsentText>
                    By providing your details, you consent to us contacting you
                    about our products and services.{' '}
                    {_get(window.ORGBranding, 'defaultBrand.legal.groupName')}’s
                    <PrivacyPolicy
                      href={_get(
                        window.ORGBranding,
                        'defaultBrand.legal.privacyPolicyLink'
                      )}
                      target="_blank"
                    >
                      Privacy Policy
                    </PrivacyPolicy>
                    contains information about how you can access and correct
                    your personal information, how to make a complaint, and how
                    we deal with complaints.
                  </ConsentText>
                  <SubmitButton
                    variant="primary"
                    type="submit"
                    label="Request Demo"
                    size="small"
                    disabled={requesting || !agreed}
                    loading={requesting}
                  />
                </Column>
              </Row>
            </TermsColumn>
          </OuterRow>
        </form>
      </Container>
    );
  }
}

export default LoginDemo;



// WEBPACK FOOTER //
// ./src/features/login/login-demo/LoginDemo.js